import apiInstance from '../utils/axiosConfig';

const ProfessionalService = {
    // ==============================================
    // CRÉATION
    // ==============================================

    /**
     * Créer un professionnel avec fichiers (multipart)
     * @param {Object} professionalRequestDto - Données JSON du professionnel
     * @param {Object} files - Objet contenant les fichiers à uploader
     */
    createProfessionalWithFiles: async (professionalRequestDto, files = {}) => {
        try {
            const formData = new FormData();

            // Ajouter les données JSON sous la clé 'professionalData' (attendu par le backend)
            formData.append('professionalData', JSON.stringify(professionalRequestDto));

            // Ajouter les fichiers (si présents)
            if (files.profilePhoto) formData.append('profilePhoto', files.profilePhoto);
            if (files.identificationFrontDoc) formData.append('identificationFrontDoc', files.identificationFrontDoc);
            if (files.identificationBackDoc) formData.append('identificationBackDoc', files.identificationBackDoc);
            if (files.criminalRecordFrontDoc) formData.append('criminalRecordFrontDoc', files.criminalRecordFrontDoc);
            if (files.criminalRecordBackDoc) formData.append('criminalRecordBackDoc', files.criminalRecordBackDoc);
            if (files.cv) formData.append('cv', files.cv);
            if (files.locationPlan) formData.append('locationPlan', files.locationPlan);

            const response = await apiInstance.post('/professionals/createProfessional', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error creating professional with files:', error.message || error);
            throw error;
        }
    },

    /**
     * Créer un professionnel sans fichiers (JSON pur)
     */
    createProfessional: async (professionalRequestDto) => {
        try {
            const response = await apiInstance.post('/professionals/createProfessional', professionalRequestDto, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            console.error('Error creating professional:', error.message || error);
            throw error;
        }
    },

    // ==============================================
    // RÉCUPÉRATION
    // ==============================================

    getProfessionalById: async (id) => {
        try {
            const response = await apiInstance.get(`/professionals/getProfessionalById/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching professional by id:', error.message || error);
            throw error;
        }
    },

    getProfessionalByUseRef: async (useRef) => {
        try {
            const response = await apiInstance.get(`/professionals/getProfessionalByUseRef/${useRef}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching professional by useRef:', error.message || error);
            throw error;
        }
    },

    getProfessionalByUserId: async (userId) => {
        try {
            const response = await apiInstance.get(`/professionals/getProfessionalByUserId/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching professional by userId:', error.message || error);
            throw error;
        }
    },

    getAllProfessionals: async () => {
        try {
            const response = await apiInstance.get('/professionals/getAllProfessionals');
            return response.data;
        } catch (error) {
            console.error('Error fetching all professionals:', error.message || error);
            throw error;
        }
    },

    getAllAcceptedAndAvailableProfessionals: async () => {
        try {
            const response = await apiInstance.get('/professionals/acceptedAndAvailable');
            return response.data;
        } catch (error) {
            console.error('Error fetching accepted and available professionals:', error.message || error);
            throw error;
        }
    },

    isProfessionalAccepted: async (useRef) => {
        try {
            const response = await apiInstance.get(`/professionals/isAccepted/${useRef}`);
            return response.data;
        } catch (error) {
            console.error('Error checking if professional is accepted:', error.message || error);
            throw error;
        }
    },

    // ==============================================
    // MISES À JOUR AVEC FICHIERS
    // ==============================================

    /**
     * Mettre à jour un professionnel par ID avec fichiers (multipart)
     */
    updateProfessionalWithFiles: async (id, professionalRequestDto, files = {}) => {
        try {
            const formData = new FormData();

            // Ajouter les données JSON sous la clé 'professionalData'
            formData.append('professionalData', JSON.stringify(professionalRequestDto));

            // Ajouter les fichiers (si présents)
            if (files.profilePhoto) formData.append('profilePhoto', files.profilePhoto);
            if (files.identificationFrontDoc) formData.append('identificationFrontDoc', files.identificationFrontDoc);
            if (files.identificationBackDoc) formData.append('identificationBackDoc', files.identificationBackDoc);
            if (files.criminalRecordFrontDoc) formData.append('criminalRecordFrontDoc', files.criminalRecordFrontDoc);
            if (files.criminalRecordBackDoc) formData.append('criminalRecordBackDoc', files.criminalRecordBackDoc);
            if (files.cv) formData.append('cv', files.cv);
            if (files.locationPlan) formData.append('locationPlan', files.locationPlan);

            const response = await apiInstance.put(`/professionals/updateProfessionalWithFiles/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error updating professional with files:', error.message || error);
            throw error;
        }
    },

    /**
     * Mettre à jour un professionnel par useRef avec fichiers (multipart)
     */
    updateProfessionalByUseRefWithFiles: async (useRef, professionalRequestDto, files = {}) => {
        try {
            const formData = new FormData();

            // Ajouter les données JSON sous la clé 'professionalData'
            formData.append('professionalData', JSON.stringify(professionalRequestDto));

            // Ajouter les fichiers (si présents)
            if (files.profilePhoto) formData.append('profilePhoto', files.profilePhoto);
            if (files.identificationFrontDoc) formData.append('identificationFrontDoc', files.identificationFrontDoc);
            if (files.identificationBackDoc) formData.append('identificationBackDoc', files.identificationBackDoc);
            if (files.criminalRecordFrontDoc) formData.append('criminalRecordFrontDoc', files.criminalRecordFrontDoc);
            if (files.criminalRecordBackDoc) formData.append('criminalRecordBackDoc', files.criminalRecordBackDoc);
            if (files.cv) formData.append('cv', files.cv);
            if (files.locationPlan) formData.append('locationPlan', files.locationPlan);

            const response = await apiInstance.put(`/professionals/updateProfessionalByUseRefWithFiles/${useRef}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error updating professional by useRef with files:', error.message || error);
            throw error;
        }
    },

    // ==============================================
    // MISES À JOUR SANS FICHIERS (JSON pur)
    // ==============================================

    updateProfessional: async (id, professionalRequestDto) => {
        try {
            const response = await apiInstance.put(`/professionals/updateProfessional/${id}`, professionalRequestDto, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating professional:', error.message || error);
            throw error;
        }
    },

    updateProfessionalByUseRef: async (useRef, professionalRequestDto) => {
        try {
            const response = await apiInstance.put(`/professionals/updateProfessionalByUseRef/${useRef}`, professionalRequestDto, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            console.error('Error updating professional by useRef:', error.message || error);
            throw error;
        }
    },

    // ==============================================
    // SUPPRESSION
    // ==============================================

    deleteProfessional: async (id) => {
        try {
            await apiInstance.delete(`/professionals/deleteProfessional/${id}`);
        } catch (error) {
            console.error('Error deleting professional:', error.message || error);
            throw error;
        }
    },

    deleteProfessionalByUseRef: async (useRef) => {
        try {
            await apiInstance.delete(`/professionals/deleteProfessionalByUseRef/${useRef}`);
        } catch (error) {
            console.error('Error deleting professional by useRef:', error.message || error);
            throw error;
        }
    }
};

export default ProfessionalService;